/* eslint-disable camelcase */
import { ScaleLinear } from 'd3-scale'
import { bisector } from 'd3-array'
import { format } from 'd3-format'
import { Subscription } from 'rxjs'

import TrackChart from '../common/TrackChart'
import { css } from 'emotion'
import BHA from './BHA'
import Tooltip from '../common/Tooltip'
import { calculateBHA } from './calculateBHA'

import { BHAState, IBHA } from './types'
import { COLUMNS, HEADERS } from '../trajectory/config'
import { ISurvey } from '../../types'

const mainCss = css`
    g.survey {
        path,
        circle {
            stroke: #333;
            stroke-width: 1px;
            stroke-linejoin: round;
        }
        body.dark-theme & {
            stroke: #f5da68;
        }
    }
`

const style = css`
    g.bha {
        //transition: transform 1s;
    }
    text.bitDepth {
        //transition: y 1s;
    }
`

const formatter = format('.2f')

export default class BHAChart extends TrackChart<any> {
    private bha: IBHA[] | undefined
    private state: BHAState
    private lastBitDepth: number

    init(): void {
        super.init()
        this.el.classed(mainCss, true)
        this.tooltip = new Tooltip<any>()
    }

    get internalAxis(): boolean {
        return true
    }

    get isVertical(): boolean {
        return true
    }

    onMouseLeave(): void {
        super.onMouseLeave()
        this._svg.selectAll('line.top').remove()
        this._svg.selectAll('line.bottom').remove()
    }

    onMouseMove(mouse: [number, number], scale: ScaleLinear<number, number>): void {
        const md = scale.invert(mouse[1])
        if (this.bha) {
            const bisect = bisector((d: IBHA) => d.top).left
            const found = bisect(this.bha, md)
            const depth = this.state.bitDepth || this.state.final_md
            if (this.bha[found - 1] && md < depth) {
                const component = this.bha[found - 1]
                const contents = this._contents.node()
                if (contents) {
                    const offset = contents.getBoundingClientRect()
                    this.tooltip.setOffset({
                        left: offset.left + (this.isVertical ? this.axisSize : 0),
                        top: offset.top
                    })

                    this._svg
                        .selectAll('line.top')
                        .data([null])
                        .join('line')
                        .classed('top', true)
                        .attr('stroke', '#ccc')
                        .attr('x1', 0)
                        .attr('x2', this.dataWidth)
                        .attr('shape-rendering', 'crispEdges')
                        .attr('y1', scale(component.top) + this.margin.top)
                        .attr('y2', scale(component.top) + this.margin.top)

                    this._svg
                        .selectAll('line.bottom')
                        .data([null])
                        .join('line')
                        .classed('bottom', true)
                        .attr('stroke', '#ccc')
                        .attr('x1', 0)
                        .attr('x2', this.dataWidth)
                        .attr('shape-rendering', 'crispEdges')
                        .attr('y1', scale(component.bottom) + this.margin.top)
                        .attr('y2', scale(component.bottom) + this.margin.top)

                    const legend = [
                        {
                            description: 'top',
                            value: formatter(component.top) + this.state.unit
                        },
                        {
                            description: 'bottom',
                            value: formatter(component.bottom) + this.state.unit
                        },
                        {
                            description: 'length',
                            value: formatter(component.length) + this.state.unit
                        }
                    ]

                    if (component.component_id) {
                        legend.push({
                            description: 'component_id',
                            value: component.component_id
                        })
                    }

                    if (component.inner_diameter) {
                        legend.push({
                            description: 'Inner Diameter',
                            value: component.inner_diameter + 'in'
                        })
                    }

                    if (component.outer_diameter) {
                        legend.push({
                            description: 'Outer Diameter',
                            value: component.outer_diameter + 'in'
                        })
                    }

                    this.tooltip.render(legend, component.component_type, mouse)
                }
            } else {
                this._svg.selectAll('line.top').remove()
                this._svg.selectAll('line.bottom').remove()
                this.tooltip.hide()
            }
        }
    }

    onMouseOver = (event: MouseEvent, dt: ISurvey): void => {
        const items = COLUMNS.filter(col => Object.prototype.hasOwnProperty.call(dt, col)).map(col => ({
            description: HEADERS[col],
            value: dt[col] + this.state.survey_uom[col]
        }))
        this.tooltip.setOffset({
            left: 0,
            top: 0
        })
        this.tooltip.render(items, 'Survey', [event.pageX, event.pageY])
    }

    render(data: BHAState): void {
        this.state = data

        const contents = this._contents.node()
        if (contents) {
            const offset = contents.getBoundingClientRect()
            this.tooltip.setOffset({
                left: offset.left + (this.isVertical ? this.axisSize : 0),
                top: offset.top
            })
        }

        super.render(data)

        const scale = this.scale

        // this.plotHorizontalLines(scale, scale.ticks(10));

        let depth = data.bitDepth || data.final_md
        if (depth) {
            if (this.lastBitDepth !== depth) {
                this.onMouseLeave()
                this.lastBitDepth = depth
            }

            this._contents
                .selectAll('div.bha')
                .data([null])
                .join('div')
                .classed('bha', true)
                // .style('border', '1px solid #f0f')
                .style('position', 'absolute')
                // .style('background', '#ccc')
                .style('width', this.innerWidth + 'px')
                .style('height', this.innerHeight + 'px')
                .style('text-align', 'center')
                .style('top', this.inner.top + 'px')
                .style('left', this.inner.left + 'px')

            const svg = this._contents
                .selectAll('div.bha')
                .selectAll('svg')
                .data([null])
                .join('svg')
                .classed(style, true)
                // .attr('viewBox', '0 0 28 10000')
                .attr('xmlns', 'http://www.w3.org/2000/svg')
                .attr('xmlns:xlink', 'http://www.w3.org/1999/xlink')
                .classed('root-bha', true)
                .attr('height', '100%')
                // .style('border', '1px solid #f0f')
                .style('max-width', '90px')
                .style('width', '100%')

            this.bha = calculateBHA(data.bha, depth)

            if (svg) {
                const bha = new BHA()
                bha.render(scale, svg.node(), this.bha, depth)
            }
        } else {
            this._contents.selectAll('div.bha').remove()
        }

        const g = this._svg.selectAll('g.data').data([null]).join('g').classed('data', true).raise()

        // define symbols
        if (this._svg.select('defs').empty()) {
            const symbol =
                '<symbol viewBox="0 0 20 20" id="survey" preserveAspectRatio="none">' +
                '<circle fill="transparent" stroke="#ccc" stroke-width="2px" stroke-linejoin="round" cx="10" cy="10" r="8"></circle>' +
                '<path stroke="#ccc"  transform="translate(10 10)" stroke-width="1px" stroke-linejoin="round" d="M-2,-3 L-5 0 L-2 3 M-5 0 H5 M2 3 L5 0 L2 -3"></path>' +
                '</symbol>'

            this._svg.append('defs').html(symbol)
        }

        g.selectAll('use.survey')
            .data(data.survey ?? [], d => d.tvd)
            .join('use')
            .classed('survey', true)
            .attr('transform', d => `translate(${this.width - 16} ${scale(d.md) - 8})`)
            .on('mouseenter', this.onMouseOver)
            .on('mouseleave', () => {
                this.tooltip.hide()
            })
            .join('use')
            .attr('width', 16)
            .attr('height', 16)
            .attr('xlink:href', d => '#survey')
            .raise()
    }

    destroy(): void {
        super.destroy()
        this._contents.selectAll('div.bha').remove()
    }
    
    renderLegend(data: BHAState): void {

    }
}
